import { memo, useEffect, useRef, useState } from 'react'
import { getCricketScoreEmbed } from '../../services/api'
import CricketScorecard from './CricketScorecard'

/* Admin copy of the player site's cricket scorecard
 * (frontend/src/components/gamedetails/CricketScoreEmbed.jsx): the live score
 * embed when its host covers the match, else the socket scorecard. Shown in the
 * game-details sidebar above My Bets (user 2026-10-22: "the one displayed in
 * the user side").
 *
 * `fallback` = whether the socket scorecard may render when there is no embed.
 * The page passes the old `scard == 1` gate, so a match the reference leaves
 * bare stays bare.
 *
 * The embed reports its own height once we say hello
 * ({ type: 'scoreboard:resize:connect', version: 1 } → { type: 'scoreboard:resize', height }).
 */
const DEFAULT_HEIGHT = 160

function CricketScoreEmbed({ gmid, started = true, fallback = true }) {
  // null = still asking; then { available, url }.
  const [embed, setEmbed] = useState(null)
  const [failed, setFailed] = useState(false)
  const [height, setHeight] = useState(DEFAULT_HEIGHT)
  const frameRef = useRef(null)

  useEffect(() => {
    let alive = true
    setEmbed(null)
    setFailed(false)
    setHeight(DEFAULT_HEIGHT)
    getCricketScoreEmbed(gmid)
      .then((res) => { if (alive) setEmbed(res) })
      .catch(() => { if (alive) setEmbed({ available: false }) })
    return () => { alive = false }
  }, [gmid])

  const origin = embed?.available && embed.url ? new URL(embed.url).origin : null

  useEffect(() => {
    if (!origin) return undefined
    let connected = false
    const onMessage = (e) => {
      if (e.origin !== origin || e.source !== frameRef.current?.contentWindow) return
      const d = e.data
      if (d && d.type === 'scoreboard:resize' && Number.isFinite(d.height)) {
        connected = true
        setHeight(d.height)
      }
    }
    window.addEventListener('message', onMessage)
    // The embed listens only once its app has started, so keep saying hello
    // until it answers (up to 60 s, same as the player site).
    let tries = 0
    const hello = setInterval(() => {
      if (connected || ++tries > 120) { clearInterval(hello); return }
      frameRef.current?.contentWindow?.postMessage({ type: 'scoreboard:resize:connect', version: 1 }, origin)
    }, 500)
    return () => {
      clearInterval(hello)
      window.removeEventListener('message', onMessage)
    }
  }, [origin])

  if (embed === null) return null
  if (!origin || failed) {
    return fallback ? <div><CricketScorecard gmid={gmid} started={started} /></div> : null
  }
  return (
    <div className="cricket-score-embed m-b-10" style={{ width: '100%' }}>
      <iframe
        ref={frameRef}
        src={embed.url}
        title="Live Score Card"
        frameBorder="0"
        onLoad={() => frameRef.current?.contentWindow?.postMessage({ type: 'scoreboard:resize:connect', version: 1 }, origin)}
        onError={() => setFailed(true)}
        style={{ width: '100%', height, border: 0, display: 'block' }}
      />
    </div>
  )
}

export default memo(CricketScoreEmbed)
